/**
 * CFS product page (build spec §6).
 * - Gallery: swipeable track of all media (images and videos) with arrows, thumbnails and a counter.
 * - Quantity stepper and variant select (updates the price on the Add button).
 * - Add to basket adds the chosen quantity on top of what is already in the basket, through
 *   assets/cfs-cart.js (fee-free state, Horizon cart events, drawer re-render).
 * - Sticky "Add to basket · £x" bar on mobile while the main button is off screen.
 * - Bundler multibuy: − / + buttons around its bundle quantity field (owner request), and stock
 *   guidance before Add to cart (end of this file).
 * - Pre-order variants (tracked, "continue selling"): "Available to pre-order" at zero stock, and a calm note
 *   with "Order N only" when the quantity (plus what is already in the basket) is more than the stock.
 * - Video deep link: #video-gallery opens the first product video in a dialog (email campaigns).
 */
import { bindQuantityField, setVariantTotal, quantityForVariant, getCartState, CART_STATE_EVENT, BUNDLE_ADDED_EVENT } from '@theme/cfs-cart';

const root = document.querySelector('.cfs-product');
const form = /** @type {HTMLFormElement | null} */ (document.querySelector('[data-cfs-product-form]'));

// Gallery: a scroll-snap track (swipe on touch); arrows, thumbnails and the counter follow it.
const gallery = document.querySelector('[data-cfs-gallery]');
const track = /** @type {HTMLElement | null} */ (gallery?.querySelector('[data-cfs-track]') ?? null);
if (gallery && track) {
  const slides = /** @type {HTMLElement[]} */ ([...track.querySelectorAll('[data-cfs-slide]')]);
  const thumbs = /** @type {HTMLElement[]} */ ([...gallery.querySelectorAll('[data-cfs-thumb]')]);
  const prev = /** @type {HTMLButtonElement | null} */ (gallery.querySelector('[data-cfs-gallery-prev]'));
  const next = /** @type {HTMLButtonElement | null} */ (gallery.querySelector('[data-cfs-gallery-next]'));
  const counter = gallery.querySelector('[data-cfs-gallery-counter]');
  let current = 0;

  const show = (/** @type {number} */ index, smooth = true) => {
    const target = slides[Math.max(0, Math.min(slides.length - 1, index))];
    if (!target) return;
    track.scrollTo({ left: target.offsetLeft, behavior: smooth && !matchMedia('(prefers-reduced-motion: reduce)').matches ? 'smooth' : 'auto' });
  };

  const update = () => {
    const index = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
    if (index === current) return;
    // Pause a video that has been swiped away.
    slides[current]?.querySelector('video')?.pause();
    current = index;
    thumbs.forEach((thumb, i) => thumb.toggleAttribute('aria-current', i === current));
    thumbs[current]?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    if (prev) prev.disabled = current === 0;
    if (next) next.disabled = current === slides.length - 1;
    if (counter) counter.textContent = `${current + 1} / ${slides.length}`;
  };

  let frame = 0;
  track.addEventListener('scroll', () => {
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(update);
  }, { passive: true });
  thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => show(i)));
  prev?.addEventListener('click', () => show(current - 1));
  next?.addEventListener('click', () => show(current + 1));
  // Video slides: a click anywhere on the slide plays/pauses (the native controls still work).
  for (const slide of slides) {
    const video = slide.querySelector('video');
    if (!video) continue;
    video.querySelector('img')?.setAttribute('alt', ''); // Shopify's no-video fallback image has no alt text
    // Phones draw their own play icon over a paused video with controls, next to ours (owner): the
    // browser's controls appear once it plays (seek, sound, full screen). Without JS they stay on.
    video.controls = false;
    video.addEventListener('play', () => {
      video.controls = true;
      slide.classList.add('is-playing', 'has-played');
    });
    video.addEventListener('pause', () => slide.classList.remove('is-playing'));
    slide.addEventListener('click', (event) => {
      // Once its controls are showing, clicks on the video itself are the browser's.
      if (event.target === video && video.controls) return;
      if (video.paused) video.play().catch(() => {});
      else video.pause();
    });
  }

  // Video deep link (email campaigns): /products/<handle>#video-gallery (also #video, #video-2)
  // opens the product's video in a dialog, ready to play, as the previous theme's popup did.
  // The dialog is built from the gallery's own video slide, and only when the hash asks for it,
  // so nothing loads otherwise. No autoplay. Closing it clears the hash and leaves the gallery on
  // the first image, so the shopper lands on a product page they can buy from.
  const videoSlides = slides.filter((slide) => slide.hasAttribute('data-cfs-video'));
  if (videoSlides.length) {
    const VIDEO_HASH = /^#video(?:-gallery)?(?:-(\d+))?$/i;
    /** @type {HTMLDialogElement | null} */
    let dialog = null;

    const openVideo = (/** @type {HTMLElement} */ slide) => {
      const source = slide.querySelector('video, iframe');
      if (!source || dialog) return;

      // Only matters when the product's first media is the video: show an image behind the dialog.
      const firstImage = slides.findIndex((s) => s.classList.contains('cfs-product__slide--image'));
      if (firstImage >= 0 && !slides[current]?.classList.contains('cfs-product__slide--image')) show(firstImage, false);

      if (source instanceof HTMLVideoElement) source.pause(); // the gallery's copy, if it was playing

      const media = /** @type {HTMLVideoElement | HTMLIFrameElement} */ (source.cloneNode(true));
      media.className = 'cfs-video-modal__media';
      media.removeAttribute('loading');
      if (media instanceof HTMLVideoElement) {
        media.preload = 'metadata'; // duration + poster, not the whole file
        media.querySelector('img')?.remove(); // Shopify's no-video fallback image has no alt text
      }

      const frame = document.createElement('div');
      frame.className = 'cfs-video-modal__frame';
      frame.style.setProperty('--cfs-video-ratio', String(Number(slide.dataset.cfsRatio) || 16 / 9));
      const player = document.createElement('div');
      player.className = 'cfs-video-modal__player';
      player.append(media);
      frame.append(player);

      // The gallery slide's own big play button, so the two look and read the same.
      const big = /** @type {HTMLButtonElement | null} */ (slide.querySelector('[data-cfs-bigplay]')?.cloneNode(true) ?? null);
      if (big && media instanceof HTMLVideoElement) {
        big.addEventListener('click', () => media.play().catch(() => {}));
        media.controls = false; // as in the gallery: one play button until it plays
        media.addEventListener('click', () => {
          if (!media.controls) media.play().catch(() => {});
        });
        media.addEventListener('play', () => {
          media.controls = true;
          player.classList.add('is-playing', 'has-played');
        });
        media.addEventListener('pause', () => player.classList.remove('is-playing'));
        player.append(big);
      }

      const closeButton = document.createElement('button');
      closeButton.type = 'button';
      closeButton.className = 'cfs-video-modal__close';
      closeButton.setAttribute('aria-label', 'Close video');
      closeButton.innerHTML = '<svg aria-hidden="true" focusable="false" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>';

      const modal = document.createElement('dialog');
      dialog = modal;
      modal.className = 'cfs-video-modal';
      modal.setAttribute('aria-label', `Product video: ${source.getAttribute('aria-label') || source.getAttribute('title') || document.title}`);
      frame.append(closeButton);
      modal.append(frame);
      closeButton.addEventListener('click', () => modal.close());
      // A click on the dimmed area (the dialog itself, outside the player) closes it too.
      modal.addEventListener('click', (event) => {
        if (event.target === modal) modal.close();
      });
      // Esc, the close button, the dimmed area and Back (hashchange) all end here.
      modal.addEventListener('close', () => {
        if (media instanceof HTMLVideoElement) {
          media.pause();
          media.replaceChildren(); // drop the <source> ...
          media.load(); // ... and abort the download
        }
        modal.remove();
        dialog = null;
        document.documentElement.classList.remove('cfs-video-open');
        if (VIDEO_HASH.test(location.hash)) history.replaceState(history.state, '', location.pathname + location.search);
        // Back to where the video lives: its thumbnail, or the gallery on phones (thumbnails hidden).
        const thumb = thumbs[slides.indexOf(slide)];
        (thumb?.offsetParent ? thumb : track).focus({ preventScroll: true });
      });

      document.body.append(modal);
      document.documentElement.classList.add('cfs-video-open');
      modal.showModal();
      (big ?? closeButton).focus({ preventScroll: true });
    };

    const openFromHash = () => {
      const match = VIDEO_HASH.exec(location.hash);
      if (!match) {
        dialog?.close();
        return;
      }
      const slide = videoSlides[Number(match[1] ?? 1) - 1] ?? videoSlides[0];
      if (slide) openVideo(slide);
    };
    window.addEventListener('hashchange', openFromHash);
    openFromHash();
  }

  track.addEventListener('keydown', (event) => {
    if (event.key === 'ArrowRight') show(current + 1);
    if (event.key === 'ArrowLeft') show(current - 1);
  });
}

if (form && root) {
  const variantInput = /** @type {HTMLInputElement | HTMLSelectElement} */ (form.querySelector('[data-cfs-variant-input]'));
  // "£5.98 in your basket" under the buttons (owner, 10 Oct 2026; replaces "N in your basket · View basket")
  const message = /** @type {HTMLElement | null} */ (form.querySelector('[data-cfs-product-message]'));
  // Errors and stock notes ("All 7 we have are in your basket.") get their own box above it
  const errorBox = document.createElement('p');
  errorBox.className = 'cfs-product__message';
  errorBox.dataset.error = 'true';
  errorBox.setAttribute('role', 'alert');
  message?.before(errorBox);
  /** @param {string} text */
  const setError = (text) => {
    if (errorBox.textContent !== text) errorBox.textContent = text;
  };
  const addButtons = /** @type {HTMLButtonElement[]} */ ([...document.querySelectorAll('[data-cfs-add-button], [data-cfs-sticky-button]')]);
  const steppers = /** @type {HTMLElement[]} */ ([...document.querySelectorAll('[data-cfs-page-stepper]')]);
  const qtyEls = [...document.querySelectorAll('[data-cfs-page-qty]')];
  const increaseButtons = /** @type {HTMLButtonElement[]} */ ([...document.querySelectorAll('[data-cfs-page-step="1"]')]);
  const prices = [...document.querySelectorAll('[data-cfs-add-price]')];
  const mainPrice = document.querySelector('[data-cfs-price]');
  const stickyPrice = document.querySelector('[data-cfs-sticky-price]');

  /** The element that carries the chosen variant's data attributes (the select's option, or the hidden input). */
  const variantSource = () => (variantInput instanceof HTMLSelectElement ? variantInput.selectedOptions[0] : variantInput);
  /** Unit price (pence) of the chosen variant. */
  const unitCents = () => Number(variantSource()?.dataset.priceCents) || 0;
  const format = (/** @type {number} */ cents) => {
    const amount = (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return (form.dataset.moneyFormat || '£{{amount}}')
      .replace(/\{\{\s*amount\s*\}\}/, amount)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, String(Math.round(cents / 100)));
  };
  // Stock limit (owner, 9 Oct 2026): a tracked variant that stops selling at 0 carries data-max (stock on
  // hand). + greys out once the basket holds all of it, and a note says so. Pre-order and untracked
  // variants have no data-max and are never limited here.
  const maxStock = () => {
    const max = variantSource()?.dataset.max;
    return max === undefined || max === '' ? Infinity : Math.max(0, Number(max));
  };
  /** "The last one is in your basket." / "All 5 we have are in your basket." (owner, 25 Oct 2026) @param {number} max */
  const allInBasket = (max) => (max === 1 ? 'The last one is in your basket.' : `All ${max} we have are in your basket.`);
  // The whole product was sold out when the page loaded (the back-in-stock box is only rendered then)
  const soldOutOnLoad = Boolean(root.querySelector('[data-cfs-restock]'));

  // Pre-order variants (tracked, "continue selling"; Liquid sets data-preorder and data-stock on them).
  // When the basket holds more than the stock on hand, a calm note says how many are in stock and how
  // many are on pre-order, and offers "Order N only". With no stock at all it says "On pre-order" before
  // anything is added. Normal variants never show any of it.
  const preorderBox = /** @type {HTMLElement | null} */ (form.querySelector('[data-cfs-preorder]'));
  const preorderHead = preorderBox?.querySelector('[data-cfs-preorder-head]') ?? null;
  const preorderCopy = preorderBox?.querySelector('[data-cfs-preorder-copy]') ?? null;
  const preorderOnly = /** @type {HTMLButtonElement | null} */ (preorderBox?.querySelector('[data-cfs-preorder-only]') ?? null);
  const preorderLive = form.querySelector('[data-cfs-preorder-live]');
  const stockLabel = document.querySelector('[data-cfs-stock-label]');
  const stockText = document.querySelector('[data-cfs-stock-text]');
  const preorderIds = [preorderHead?.id, preorderCopy?.id].filter(Boolean).join(' ');
  const verbs = /** @type {HTMLElement[]} */ ([...document.querySelectorAll('[data-cfs-add-verb]')]);
  const suffixes = /** @type {HTMLElement[]} */ ([...document.querySelectorAll('[data-cfs-add-suffix]')]);
  let liveTimer = 0;

  /** Stock on hand (0 or more) when the chosen variant is a pre-order variant, otherwise -1. */
  const preorderStock = () => {
    const source = variantSource();
    return source?.dataset.preorder === 'true' ? Math.max(0, Number(source.dataset.stock) || 0) : -1;
  };

  /** @param {number} inBasket @param {boolean} announce false for changes the shopper did not just make */
  const renderPreorder = (inBasket, announce) => {
    if (!preorderBox) return;
    const stock = preorderStock();

    if (stockLabel && stockText && !stockLabel.classList.contains('cfs-product__stock--out')) {
      const empty = stock === 0;
      if (stockLabel.classList.contains('cfs-product__stock--preorder') !== empty) stockLabel.classList.toggle('cfs-product__stock--preorder', empty);
      const label = empty ? 'Available to pre-order' : 'In stock';
      if (stockText.textContent !== label) stockText.textContent = label;
    }

    // CFS (owner, 11 Oct 2026): the add buttons say "Pre-order" when there is no stock on the shelf
    for (const verb of verbs) {
      const text = stock === 0 ? 'Pre-order' : verb.dataset.verbAdd;
      if (verb.textContent !== text) verb.textContent = text;
    }
    for (const suffix of suffixes) {
      if (suffix.hidden !== (stock === 0)) suffix.hidden = stock === 0;
    }

    const showNote = stock >= 0 && Math.max(inBasket, 1) > stock;
    let head = '';
    if (showNote) {
      head = stock < 1 || inBasket === 0 ? 'On pre-order' : `Your basket has ${inBasket} (${stock} in stock + ${inBasket - stock} on pre-order)`;
      if (preorderHead && preorderHead.textContent !== head) preorderHead.textContent = head;
    }
    if (preorderBox.hidden !== !showNote) preorderBox.hidden = !showNote;

    if (preorderOnly) {
      const show = showNote && stock >= 1 && inBasket > stock;
      if (preorderOnly.hidden !== !show) preorderOnly.hidden = !show;
      if (show) {
        const label = `Order ${stock} only`;
        if (preorderOnly.textContent !== label) preorderOnly.textContent = label;
        preorderOnly.dataset.qty = String(stock);
      }
    }

    const describedBy = showNote ? preorderIds : '';
    for (const button of addButtons) {
      if ((button.getAttribute('aria-describedby') ?? '') !== describedBy) {
        if (describedBy) button.setAttribute('aria-describedby', describedBy);
        else button.removeAttribute('aria-describedby');
      }
    }

    window.clearTimeout(liveTimer);
    const live = showNote ? `${head}. ${preorderCopy?.textContent ?? ''}` : '';
    if (announce) {
      liveTimer = window.setTimeout(() => {
        if (preorderLive && preorderLive.textContent !== live) preorderLive.textContent = live;
      }, 300);
    } else if (preorderLive?.textContent) {
      preorderLive.textContent = '';
    }
  };

  // Basket quantity of the chosen variant: shown at once (optimistic), sent after a short pause so quick
  // taps become one request, as on the cards (assets/cfs-product-card.js).
  const COALESCE_MS = 300;
  let confirmed = 0;
  let wanted = 0;
  let shown = 0;
  let timer = 0;
  let inFlight = false;
  let limited = false; // MinMaxify category limit reached
  let stockNote = '';

  /** Writes only what changed (apps observe the whole page). @param {number} quantity @param {boolean} announce */
  const render = (quantity, announce) => {
    const wasStepper = shown > 0;
    shown = quantity;
    const inBasket = quantity > 0;
    for (const el of qtyEls) {
      if (el instanceof HTMLInputElement) {
        // Not while the shopper is typing in it
        if (document.activeElement !== el && el.value !== String(quantity)) el.value = String(quantity);
      } else if (el.textContent !== String(quantity)) el.textContent = String(quantity);
    }
    for (const el of steppers) if (el.hidden !== !inBasket) el.hidden = !inBasket;
    for (const button of addButtons) if (button.hidden !== inBasket) button.hidden = inBasket;

    const max = maxStock();
    const atMax = quantity >= max;
    for (const button of increaseButtons) {
      const off = atMax || limited;
      if ((button.getAttribute('aria-disabled') === 'true') !== off) {
        if (off) button.setAttribute('aria-disabled', 'true');
        else button.removeAttribute('aria-disabled');
      }
    }
    for (const button of addButtons) if (button.disabled !== (max === 0)) button.disabled = max === 0;
    // Stock note: replaces only its own text (a cart error stays until the next change). A product sold
    // out when the page loaded already says "Out of stock" by the price and shows the back-in-stock box,
    // so it gets no "now out of stock" note (owner, 25 Oct 2026)
    const note = max === 0 ? (soldOutOnLoad ? '' : 'Sorry, this is now out of stock.') : inBasket && atMax ? allInBasket(max) : '';
    if (note !== stockNote && (errorBox.textContent === stockNote || note)) setError(note);
    stockNote = note;

    if (message) {
      const text = inBasket ? `${quantity} in your basket` : ''; // no amount (owner, 10 Oct 2026)
      if (message.textContent !== text) message.textContent = text;
    }
    renderPreorder(quantity, announce);

    // Focus follows the control that replaced the one pressed (Add ↔ stepper)
    const active = document.activeElement;
    if (wasStepper !== inBasket && active instanceof HTMLElement && (active.closest('[data-cfs-page-stepper]') || addButtons.includes(/** @type {any} */ (active)))) {
      const home = active.closest('.cfs-sticky-add') ?? form;
      const next = inBasket ? home.querySelector('[data-cfs-page-step="1"]') : home.querySelector('[data-cfs-add-button], [data-cfs-sticky-button]');
      /** @type {HTMLElement | null} */ (next)?.focus({ preventScroll: true });
    }
  };

  const syncFromCart = () => {
    confirmed = quantityForVariant(variantInput.value);
    wanted = confirmed;
    render(confirmed, false);
  };

  const flush = async () => {
    if (inFlight || wanted === confirmed) return;
    inFlight = true;
    const variantId = variantInput.value;
    const target = wanted;
    for (const el of [...addButtons, ...steppers]) el.setAttribute('aria-busy', 'true');
    try {
      const cart = await setVariantTotal({ variantId, total: target, source: form });
      confirmed = quantityForVariant(variantId, cart);
      if (confirmed !== target && wanted === target) {
        // The cart accepted fewer than asked (stock changed since the page loaded)
        wanted = confirmed;
        render(confirmed, false);
        setError(`Only ${confirmed} available.`);
      }
    } catch (error) {
      confirmed = quantityForVariant(variantId);
      if (wanted === target) {
        wanted = confirmed;
        render(confirmed, false);
      }
      if (/** @type {any} */ (error)?.detail?.limit) {
        limited = true;
        render(shown, false);
      }
      stockNote = '';
      setError(/** @type {Error} */ (error)?.message || 'Could not update your basket');
    } finally {
      inFlight = false;
      for (const el of [...addButtons, ...steppers]) el.removeAttribute('aria-busy');
      if (wanted !== confirmed && !timer) flush();
    }
  };

  /** @param {number} quantity */
  const request = (quantity) => {
    const max = maxStock();
    if (quantity > shown && (quantity > max || limited)) {
      // Past the stock or the category limit: say so instead of asking the cart
      if (limited) return;
      setError(max === 0 ? 'Sorry, this is now out of stock.' : shown > 0 ? allInBasket(max) : `Only ${max} available.`);
      return;
    }
    if (quantity < shown) limited = false;
    if (errorBox.textContent !== stockNote) setError(stockNote);
    wanted = quantity;
    render(quantity, true);
    clearTimeout(timer);
    const delay = confirmed === 0 && quantity === 1 ? 0 : COALESCE_MS;
    timer = window.setTimeout(() => {
      timer = 0;
      flush();
    }, delay);
  };

  // Typed quantity (owner, 10 Oct 2026): saved on Enter or on leaving the field; past the stock it stops
  // at the stock and says so; 0 removes it; left empty, the number comes back.
  const typed = /** @type {HTMLInputElement | null} */ (form.querySelector('input[data-cfs-page-qty]'));
  if (typed) {
    bindQuantityField(typed, (value) => {
      const max = maxStock();
      let next = value ?? shown;
      if (next > max) next = max;
      if (next === shown) {
        typed.value = String(shown);
        if (value !== null && value > max) setError(max === 0 ? 'Sorry, this is now out of stock.' : allInBasket(max));
        return;
      }
      request(next); // at the stock, the stock note says "All N we have are in your basket."
    });
  }

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    request(Math.max(1, shown + 1));
  });
  document.querySelector('[data-cfs-sticky-button]')?.addEventListener('click', () => request(Math.max(1, shown + 1)));
  for (const button of document.querySelectorAll('[data-cfs-page-step]')) {
    button.addEventListener('click', () => request(Math.max(0, shown + Number(/** @type {HTMLElement} */ (button).dataset.cfsPageStep))));
  }
  preorderOnly?.addEventListener('click', () => {
    request(Math.max(1, Number(preorderOnly.dataset.qty) || 1));
    /** @type {HTMLElement | null} */ (form.querySelector('[data-cfs-page-step="1"]'))?.focus({ preventScroll: true });
  });

  document.addEventListener(CART_STATE_EVENT, () => {
    // Ignore broadcasts while this page has its own change pending
    if (inFlight || timer) return;
    syncFromCart();
  });

  if (variantInput instanceof HTMLSelectElement) {
    variantInput.addEventListener('change', () => {
      const option = variantInput.selectedOptions[0];
      const price = option?.dataset.price ?? '';
      if (mainPrice) mainPrice.textContent = price;
      // SKU line under the title follows the variant
      const sku = option?.dataset.sku ?? '';
      const skuEl = document.querySelector('[data-cfs-sku]');
      if (skuEl) skuEl.textContent = sku;
      const skuWrap = /** @type {HTMLElement | null} */ (document.querySelector('[data-cfs-sku-wrap]'));
      if (skuWrap) skuWrap.hidden = !sku;
      if (stickyPrice) stickyPrice.textContent = price;
      for (const el of prices) el.textContent = format(unitCents());
      clearTimeout(timer);
      timer = 0;
      limited = false;
      stockNote = '';
      setError('');
      syncFromCart();
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variantInput.value);
      history.replaceState(history.state, '', url.toString());
    });
  }

  syncFromCart();

  // Sticky add bar (mobile): visible while the main Add button is out of view, below the fold.
  const sticky = /** @type {HTMLElement | null} */ (document.querySelector('[data-cfs-sticky-add]'));
  const mainActions = form.querySelector('[data-cfs-main-actions]');
  if (sticky && mainActions && 'IntersectionObserver' in window) {
    new IntersectionObserver(([entry]) => {
      sticky.hidden = entry.isIntersecting || entry.boundingClientRect.top > 0;
    }).observe(mainActions);
  }
}

// Delivery & collection: grey out options this product can't use, from Zapiet's own rules
// (window.ZapietMethods[method].status.product_conditions: tags that exclude a product).
const deliveryBox = /** @type {HTMLElement | null} */ (document.querySelector('[data-cfs-delivery]'));
if (deliveryBox) {
  const tags = (deliveryBox.dataset.productTags ?? '').split('|');
  // Zapiet methods: shipping = UK delivery, delivery = local delivery, pickup = click & collect
  const names = { shipping: 'UK delivery', delivery: 'local delivery', pickup: 'click & collect' };
  const apply = () => {
    const methods = /** @type {any} */ (window).ZapietMethods;
    if (!methods) return false;
    /** @type {string[]} */
    const blocked = [];
    for (const method of Object.keys(names)) {
      const conditions = methods[method]?.status?.product_conditions;
      const excluded = conditions?.enabled && conditions.property === 'tags'
        ? String(conditions.value ?? '').split(',').map((tag) => tag.trim()).some((tag) => tags.includes(tag))
        : false;
      // A method switched off in Zapiet (e.g. local delivery while short-staffed) isn't offered at all
      const enabled = methods[method]?.enabled !== false;
      if (excluded && enabled) blocked.push(method);
      for (const row of deliveryBox.querySelectorAll(`[data-cfs-zapiet-method="${method}"]`)) {
        /** @type {HTMLElement} */ (row).hidden = !enabled;
        row.classList.toggle('is-unavailable', excluded);
      }
    }
    const note = /** @type {HTMLElement | null} */ (deliveryBox.querySelector('[data-cfs-delivery-note]'));
    if (note) {
      note.hidden = blocked.length === 0;
      const offered = Object.keys(names).filter((m) => methods[m]?.enabled !== false && !blocked.includes(m));
      if (blocked.length && offered.length) {
        note.textContent = `This item is ${offered.map((m) => /** @type {any} */ (names)[m]).join(' or ')} only.`;
      } else if (blocked.length) {
        note.textContent = 'This item is not available online at the moment.';
      }
    }
    return true;
  };
  // Zapiet loads after the theme: try now, then a few times while it starts.
  if (!apply()) {
    let tries = 0;
    const timer = window.setInterval(() => {
      if (apply() || ++tries > 20) window.clearInterval(timer);
    }, 500);
  }
}

// "Customers also bought": load recommendations when the row is near the viewport. Complementary
// products (Search & Discovery) go first and rename the row "Goes well with"; the related ones fill
// the rest. Products already in the shelf row above are skipped, so nothing shows twice.
const recs = /** @type {HTMLElement | null} */ (document.querySelector('[data-cfs-recs]'));
if (recs?.dataset.url) {
  /** @param {string | undefined} url */
  const fetchItems = async (url) => {
    if (!url) return [];
    try {
      const response = await fetch(url);
      if (!response.ok) return [];
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      return [...doc.querySelectorAll('[data-cfs-recs-items] > li')];
    } catch {
      return []; // Recommendations are optional.
    }
  };
  const load = async () => {
    const [pairs, related] = await Promise.all([fetchItems(recs.dataset.pairUrl), fetchItems(recs.dataset.url)]);
    const seen = new Set(
      [...document.querySelectorAll('.cfs-row:not([data-cfs-recs]) [data-product-id]')].map(
        (card) => /** @type {HTMLElement} */ (card).dataset.productId
      )
    );
    const pick = (/** @type {Element[]} */ items) =>
      items.filter((li) => {
        const id = /** @type {HTMLElement | null} */ (li.querySelector('[data-product-id]'))?.dataset.productId;
        if (!id || seen.has(id)) return false;
        seen.add(id);
        return true;
      });
    const chosenPairs = pick(pairs);
    const items = [...chosenPairs, ...pick(related)].slice(0, Number(recs.dataset.limit) || 8);
    if (items.length === 0) return;
    if (chosenPairs.length > 0) {
      const title = recs.querySelector('.cfs-row__title');
      if (title) title.textContent = 'Goes well with';
    }
    recs.querySelector('[data-cfs-recs-track]')?.append(...items.map((li) => document.importNode(li, true)));
    recs.hidden = false;
  };
  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        observer.disconnect();
        load();
      }
    }, { rootMargin: '400px 0px' });
    observer.observe(recs.parentElement ?? recs);
  } else {
    load();
  }
}

// Bundler multibuy (app block): its bundle quantity is a bare number field, so add − / + buttons
// like the main stepper (owner request). They only change the field's value; Bundler still reads
// it on "Add to cart". Bundler re-renders its widget, so new fields are picked up as they appear.
const BUNDLE_QTY = '.cfs-product__app-block .bndlr-add-to-cart-quantity-input';
const STEP_ICONS = {
  '-1': '<path d="M5 12h14"/>',
  '1': '<path d="M12 5v14M5 12h14"/>',
};

/** @param {HTMLInputElement} input @param {number} step */
function stepButton(input, step) {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'cfs-bundle-qty__btn';
  button.setAttribute('aria-label', step < 0 ? 'Fewer bundles' : 'More bundles');
  button.innerHTML = `<svg aria-hidden="true" focusable="false" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round">${STEP_ICONS[String(step)]}</svg>`;
  button.addEventListener('click', () => {
    const min = Number(input.min) || 1;
    const value = Math.min(99, Math.max(min, (parseInt(input.value, 10) || min) + step));
    input.value = String(value);
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });
  return button;
}

function enhanceBundleQuantity() {
  for (const input of /** @type {NodeListOf<HTMLInputElement>} */ (document.querySelectorAll(BUNDLE_QTY))) {
    if (input.dataset.cfsStepper) continue;
    input.dataset.cfsStepper = 'true';
    input.setAttribute('aria-label', 'Number of bundles');
    input.inputMode = 'numeric';
    input.closest('.bndlr-floating-label')?.classList.add('cfs-bundle-qty');
    input.before(stepButton(input, -1));
    input.after(stepButton(input, 1));
  }
}

const appBlocks = document.querySelectorAll('.cfs-product__app-block');
if (appBlocks.length) {
  enhanceBundleQuantity();
  const watcher = new MutationObserver(enhanceBundleQuantity);
  for (const block of appBlocks) watcher.observe(block, { childList: true, subtree: true });
}

/*
 * Bundler multibuy: stock guidance before Add to cart (owner, 8 Oct 2026). A mix-and-match bundle
 * can ask for more of an item than is in stock (e.g. 2 sets of 1 tuna + 2 chicken with 1 tuna left);
 * Shopify would then add part of it (assets/cfs-cart.js trims that back to whole sets). Here the
 * shopper sees it coming and can change the mix:
 * - each item shows "Only N left" (5 or fewer), "Out of stock", or "All N in your basket"; its +
 *   is greyed out once one set has as many as can be ordered (a click on it explains why);
 * - "Your bundle" with more sets than stock allows: a message names the item, offers "Add N sets
 *   instead", and Add to cart waits; with an item that has none left, it asks to swap that item;
 * - the sets + stops at the most sets available.
 * Stock comes from /products/<handle>?view=cfs-stock (templates/product.cfs-stock.liquid): no
 * limit for untracked stock or pre-orders; what is already in the basket counts against it. If
 * stock can't be read, nothing is shown (the trim after adding still keeps whole sets).
 */
const BUNDLE_WIDGET = '.cfs-product__app-block .bndlr-container.bndlr-mixnmatch';
const LOW_STOCK = 5;
/** @type {Map<string, { max: number | null, title: string }>} variant id → stock */
const bundleStock = new Map();
/** @type {Map<string, Promise<void>>} product handle → stock request */
const stockRequests = new Map();
/**
 * A short message shown in place of the stock message: "warn" after a click on a greyed-out +
 * (it wins over the stock message), "done" after a bundle add (shown while there is no problem).
 * @type {WeakMap<Element, { text: string, kind: 'warn' | 'done', until: number }>}
 */
const bundleHints = new WeakMap();
/** @param {Element} widget @param {string} text @param {'warn' | 'done'} kind @param {number} ms */
function setBundleHint(widget, text, kind, ms) {
  bundleHints.set(widget, { text, kind, until: Date.now() + ms });
  window.setTimeout(scheduleBundleStock, ms + 50);
  scheduleBundleStock();
}

/** @param {string} handle */
function loadBundleStock(handle) {
  let request = stockRequests.get(handle);
  if (!request) {
    const root = window.Shopify?.routes?.root ?? '/';
    request = fetch(`${root}products/${encodeURIComponent(handle)}?view=cfs-stock`)
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (!data?.variants) return;
        for (const [id, variant] of Object.entries(data.variants)) {
          bundleStock.set(String(id), { max: variant.available ? variant.max : 0, title: String(data.title ?? '').replace(/\s+-\s+/g, ' ') });
        }
      })
      .catch(() => {});
    stockRequests.set(handle, request);
  }
  return request;
}

/** Quantity of a variant already in the basket (all its lines). @param {string} variantId */
function basketQuantity(variantId) {
  let total = 0;
  for (const item of getCartState()?.items ?? []) if (String(item.variant_id) === variantId) total += item.quantity;
  return total;
}

/** How many more of a variant can be ordered (Infinity: no limit, or stock unknown). @param {string} variantId */
function orderable(variantId) {
  const stock = bundleStock.get(variantId);
  if (!stock || stock.max === null) return Infinity;
  return Math.max(0, stock.max - basketQuantity(variantId));
}

const plural = (/** @type {number} */ n, /** @type {string} */ word) => `${n} ${word}${n === 1 ? '' : 's'}`;

/** e.g. "Felix … Tuna has only 1 left (plus 1 already in your basket)" @param {string} variantId */
function stockPhrase(variantId) {
  const title = bundleStock.get(variantId)?.title || 'This item';
  const left = orderable(variantId);
  const inBasket = basketQuantity(variantId);
  if (left === 0) return inBasket > 0 ? `all the ${title} in stock ${inBasket === 1 ? 'is' : 'are'} already in your basket` : `${title} is out of stock`;
  return `${title} has only ${left} left${inBasket > 0 ? ` (plus ${inBasket} already in your basket)` : ''}`;
}

/** @param {Element} widget */
function readBundle(widget) {
  const cards = [...widget.querySelectorAll('.bndlr-mnm-available-products .bndlr-product')].map((card) => {
    const variantId = /** @type {HTMLSelectElement | null} */ (card.querySelector('.bndlr-select-variant'))?.value ?? '';
    const href = card.querySelector('a.bndlr-product-title, a.bndlr-product-image-url')?.getAttribute('href') ?? '';
    const handle = /\/products\/([^/?#]+)/.exec(href)?.[1] ?? '';
    return { card, variantId, handle };
  });
  /** @type {Map<string, number>} variant → quantity in one set */
  const picked = new Map();
  for (const row of widget.querySelectorAll('.bndlr-mnm-selected-products .bndlr-product[data-variant-id]')) {
    const id = row.getAttribute('data-variant-id') ?? '';
    const qty = Number(row.querySelector('.bndlr-product-quantity')?.getAttribute('product-quantity')) || 1;
    if (id) picked.set(id, (picked.get(id) ?? 0) + qty);
  }
  const input = /** @type {HTMLInputElement | null} */ (widget.querySelector('.bndlr-add-to-cart-quantity-input'));
  const sets = Math.max(1, parseInt(input?.value ?? '1', 10) || 1);
  return { cards, picked, input, sets };
}

/**
 * Items per set for a mix-and-match bundle ("Buy any 3" = 3), from Bundler's own settings
 * (window.bndlr.getBundles(), matched by the widget's data-bundle-name); 0 when not known.
 * @param {Element} widget
 */
function bundleSetSize(widget) {
  const name = widget.getAttribute('data-bundle-name') ?? '';
  let bundles;
  try {
    bundles = /** @type {any} */ (window).bndlr?.getBundles?.();
  } catch {
    bundles = null;
  }
  const list = Array.isArray(bundles) ? bundles : Object.values(bundles ?? {});
  const bundle = list.find((b) => b?.name === name && b?.minimum_requirements === 'n_products');
  return Number(bundle?.minimum_requirements_n_max_products) || Number(bundle?.minimum_requirements_num) || 0;
}

/** The most sets the picked mix allows, and the item that limits it. @param {Map<string, number>} picked */
function maxSets(picked) {
  let most = Infinity;
  let limiting = '';
  for (const [id, qty] of picked) {
    const sets = Math.floor(orderable(id) / qty);
    if (sets < most) {
      most = sets;
      limiting = id;
    }
  }
  return { most, limiting };
}

/** Sets or clears a "true" attribute only when it changes (Bundler watches its widget). */
const flag = (/** @type {Element} */ el, /** @type {string} */ name, /** @type {boolean} */ on) => {
  if (on) {
    if (el.getAttribute(name) !== 'true') el.setAttribute(name, 'true');
  } else if (el.hasAttribute(name)) el.removeAttribute(name);
};

/** @param {Element} widget */
function applyBundleStock(widget) {
  const { cards, picked, input, sets } = readBundle(widget);
  if (!cards.length || !cards.every(({ variantId }) => bundleStock.has(variantId))) return; // stock not known (yet)

  // Items in one set: "Buy any 3" takes exactly 3 (Bundler doesn't stop more being picked)
  const size = bundleSetSize(widget);
  let inSet = 0;
  for (const qty of picked.values()) inSet += qty;
  const setFull = size > 0 && inSet >= size;

  // Items: stock note, and whether + can add one more to the set
  for (const { card, variantId } of cards) {
    const left = orderable(variantId);
    const inBasket = basketQuantity(variantId);
    let note = '';
    if (left === 0) note = inBasket > 0 ? `All ${inBasket} in your basket` : 'Out of stock';
    else if (left <= LOW_STOCK) note = `Only ${left} left`;
    let chip = /** @type {HTMLElement | null} */ (card.querySelector('[data-cfs-stock-note]'));
    if (note && !chip) {
      chip = document.createElement('span');
      chip.className = 'cfs-bundle-note';
      chip.setAttribute('data-cfs-stock-note', '');
      card.querySelector('.bndlr-product-price')?.after(chip);
    }
    if (chip) {
      if (chip.textContent !== note) chip.textContent = note;
      if (chip.hidden !== !note) chip.hidden = !note;
    }
    flag(card, 'data-cfs-out', left === 0);
    const plus = card.querySelector('.bndlr-add-to-bundle');
    const noStock = (picked.get(variantId) ?? 0) + 1 > left;
    const why = setFull ? 'set' : noStock ? 'stock' : '';
    const rowPlus = widget.querySelector(`[data-cfs-pick-qty][data-variant-id="${variantId}"] [data-cfs-pick-step="1"]`);
    for (const button of [plus, rowPlus]) {
      if (!button) continue;
      flag(button, 'aria-disabled', Boolean(why));
      if (button.getAttribute('data-cfs-why') !== why) {
        if (why) button.setAttribute('data-cfs-why', why);
        else button.removeAttribute('data-cfs-why');
      }
    }
  }

  // Sets: message, "Add N sets instead"; Add to cart waits while the mix can't be ordered
  const { most, limiting } = maxSets(picked);
  const wrapper = widget.querySelector('.bndlr-mnm-add-to-cart-wrapper');
  const addButton = widget.querySelector('.bndlr-add-bundle-to-cart');
  const more = widget.querySelector('.cfs-bundle-qty__btn[aria-label="More bundles"]');
  let message = '';
  let fixTo = 0;
  if (size > 0 && inSet > size) {
    const extra = inSet - size;
    message = `This offer is for ${size} items per set: remove ${plural(extra, 'item')} from your bundle.`;
  } else if (picked.size && most === 0) {
    const phrase = stockPhrase(limiting);
    message = `${phrase.charAt(0).toUpperCase()}${phrase.slice(1)}. Remove it from your bundle and pick another item.`;
  } else if (picked.size && sets > most) {
    message = `Only ${plural(most, 'set')} available with this mix: ${stockPhrase(limiting)}. Choose fewer sets or swap it for another item.`;
    fixTo = most;
  }
  let box = /** @type {HTMLElement | null} */ (wrapper?.querySelector('[data-cfs-bundle-stock]') ?? null);
  if (wrapper && !box) {
    box = document.createElement('div');
    box.className = 'cfs-bundle-stock';
    box.setAttribute('data-cfs-bundle-stock', '');
    box.setAttribute('role', 'status');
    box.hidden = true;
    box.innerHTML = '<p class="cfs-bundle-stock__text" data-cfs-bundle-stock-text></p><button type="button" class="cfs-bundle-stock__fix" data-cfs-bundle-fix hidden></button>';
    wrapper.prepend(box);
  }
  let hint = bundleHints.get(widget);
  if (hint && hint.until <= Date.now()) {
    bundleHints.delete(widget);
    hint = undefined;
  }
  const shown = hint?.kind === 'warn' ? hint.text : message || hint?.text || '';
  const kind = hint && shown === hint.text ? hint.kind : 'warn';
  if (box) {
    const text = box.querySelector('[data-cfs-bundle-stock-text]');
    if (text && text.textContent !== shown) text.textContent = shown;
    if (box.hidden !== !shown) box.hidden = !shown;
    if (box.dataset.kind !== kind) box.dataset.kind = kind;
    const fix = /** @type {HTMLButtonElement | null} */ (box.querySelector('[data-cfs-bundle-fix]'));
    if (fix) {
      const offer = shown === message ? fixTo : 0;
      const label = offer ? `Add ${plural(offer, 'set')} instead` : '';
      if (fix.textContent !== label) fix.textContent = label;
      if (fix.hidden !== !offer) fix.hidden = !offer;
      fix.dataset.sets = String(offer);
    }
  }
  if (addButton) flag(addButton, 'aria-disabled', Boolean(message));
  if (more) flag(more, 'aria-disabled', picked.size > 0 && sets >= most);
}

/**
 * "Your bundle": a − qty + on each picked line (owner, 8 Oct 2026), so one fewer doesn't mean
 * removing the line and picking again. Bundler has no "one fewer": + presses the item's own Add
 * (so stock guidance still applies), − removes the line with Bundler's × and picks it again one
 * fewer time (at 1 it just removes it). The × stays for removing the line.
 * @param {Element} widget
 */
function enhancePickedRows(widget) {
  for (const row of widget.querySelectorAll('.bndlr-mnm-selected-products .bndlr-product[data-variant-id]')) {
    if (row.querySelector('[data-cfs-pick-qty]')) continue;
    const variantId = row.getAttribute('data-variant-id') ?? '';
    const qty = Number(row.querySelector('.bndlr-product-quantity')?.getAttribute('product-quantity')) || 1;
    const title = (row.querySelector('.bndlr-product-title')?.textContent ?? '').replace(/\s+-\s+/g, ' ').trim();
    const box = document.createElement('div');
    box.className = 'cfs-pick-qty';
    box.setAttribute('data-cfs-pick-qty', '');
    box.setAttribute('role', 'group');
    box.setAttribute('aria-label', `${title} in your bundle`);
    box.innerHTML =
      `<button type="button" class="cfs-pick-qty__btn" data-cfs-pick-step="-1" aria-label="${qty === 1 ? 'Remove' : 'One fewer'} ${title}">${STEP_SVG('-1')}</button>` +
      `<span class="cfs-pick-qty__value" aria-live="polite">${qty}</span>` +
      `<button type="button" class="cfs-pick-qty__btn" data-cfs-pick-step="1" aria-label="One more ${title}">${STEP_SVG('1')}</button>`;
    box.dataset.variantId = variantId;
    const close = row.querySelector('.bndlr-close');
    if (close) close.before(box);
    else row.append(box);
    row.classList.add('cfs-pick-row');
  }
}

/** @param {'-1' | '1'} step */
const STEP_SVG = (step) =>
  `<svg aria-hidden="true" focusable="false" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round">${STEP_ICONS[step]}</svg>`;

/** The item's own Add button in the widget. @param {Element} widget @param {string} variantId */
function itemAddButton(widget, variantId) {
  for (const card of widget.querySelectorAll('.bndlr-mnm-available-products .bndlr-product')) {
    if (/** @type {HTMLSelectElement | null} */ (card.querySelector('.bndlr-select-variant'))?.value === variantId) {
      return /** @type {HTMLElement | null} */ (card.querySelector('.bndlr-add-to-bundle'));
    }
  }
  return null;
}

/** True while a − re-picks a line (its re-adds are fewer than before, so the guard lets them through). */
let restepping = false;

/** @param {Element} button */
async function stepPicked(button) {
  const widget = button.closest(BUNDLE_WIDGET);
  const box = /** @type {HTMLElement | null} */ (button.closest('[data-cfs-pick-qty]'));
  if (!widget || !box) return;
  bundleHints.delete(widget); // a change to the bundle: earlier hints no longer apply
  const variantId = box.dataset.variantId ?? '';
  const add = itemAddButton(widget, variantId);
  if (button.getAttribute('data-cfs-pick-step') === '1') {
    add?.click(); // greyed out when no more can go in: the stock guidance explains
    return;
  }
  const row = box.closest('.bndlr-product');
  const qty = Number(row?.querySelector('.bndlr-product-quantity')?.getAttribute('product-quantity')) || 1;
  restepping = true;
  try {
    /** @type {HTMLElement | null} */ (row?.querySelector('.bndlr-close') ?? null)?.click();
    for (let i = 1; i < qty && add; i++) {
      await new Promise((resolve) => requestAnimationFrame(resolve));
      add.click();
    }
  } finally {
    restepping = false;
    scheduleBundleStock();
  }
}

let bundleFrame = 0;
function scheduleBundleStock() {
  cancelAnimationFrame(bundleFrame);
  bundleFrame = requestAnimationFrame(() => {
    for (const widget of document.querySelectorAll(BUNDLE_WIDGET)) {
      const handles = new Set(readBundle(widget).cards.map((c) => c.handle).filter(Boolean));
      const pending = [...handles].filter((h) => !stockRequests.has(h));
      if (pending.length) Promise.all(pending.map(loadBundleStock)).then(scheduleBundleStock);
      enhancePickedRows(widget);
      applyBundleStock(widget);
    }
  });
}

/** Why a + is greyed out: the set is full, or no more in stock. @param {Element} widget @param {Element} control @param {string} variantId */
function explainPlus(widget, control, variantId) {
  if (control.getAttribute('data-cfs-why') === 'set') {
    return `This offer is for ${bundleSetSize(widget)} items per set. For more, increase the number of sets below.`;
  }
  return `You can't add more: ${stockPhrase(variantId)}.`;
}

/** Stops a click (or Enter / Space) on a greyed-out control and explains it. @param {Event} event */
function guardBundleControl(event) {
  if (restepping) return;
  if (event instanceof KeyboardEvent && event.key !== 'Enter' && event.key !== ' ') return;
  const target = /** @type {Element} */ (event.target);
  const fix = target.closest?.('[data-cfs-bundle-fix]');
  if (fix) {
    const widget = fix.closest(BUNDLE_WIDGET);
    const input = /** @type {HTMLInputElement | null} */ (widget?.querySelector('.bndlr-add-to-cart-quantity-input') ?? null);
    if (input) {
      input.value = /** @type {HTMLElement} */ (fix).dataset.sets ?? '1';
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
    scheduleBundleStock();
    return;
  }
  const step = target.closest?.('[data-cfs-pick-step]');
  if (step && step.getAttribute('aria-disabled') !== 'true') {
    event.preventDefault();
    stepPicked(step);
    return;
  }
  const control = target.closest?.('.cfs-product__app-block [aria-disabled="true"]');
  if (control?.matches('[data-cfs-pick-step]')) {
    // + on a picked line with no more stock: explain it as the item's own + would
    event.preventDefault();
    event.stopImmediatePropagation();
    const widget = control.closest(BUNDLE_WIDGET);
    const variantId = /** @type {HTMLElement | null} */ (control.closest('[data-cfs-pick-qty]'))?.dataset.variantId ?? '';
    if (widget) setBundleHint(widget, explainPlus(widget, control, variantId), 'warn', 8000);
    return;
  }
  if (!control) {
    // Any other click in the widget: a hint gives way to the bundle's own message again
    const widget = target.closest?.(BUNDLE_WIDGET);
    if (widget && bundleHints.delete(widget)) scheduleBundleStock();
    return;
  }
  event.preventDefault();
  event.stopImmediatePropagation();
  const widget = control.closest(BUNDLE_WIDGET);
  const card = control.closest('.bndlr-mnm-available-products .bndlr-product');
  if (widget && card) {
    // + on an item: say how many of it can go in
    const variantId = /** @type {HTMLSelectElement | null} */ (card.querySelector('.bndlr-select-variant'))?.value ?? '';
    setBundleHint(widget, explainPlus(widget, control, variantId), 'warn', 8000);
  }
  requestAnimationFrame(() => widget?.querySelector('[data-cfs-bundle-stock]')?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }));
}

/**
 * After a bundle add (assets/cfs-cart.js): Bundler keeps the picked items, which are now in the
 * basket, so the stock check would see the same mix again and warn (owner review). Clear the
 * picks with Bundler's own remove buttons, put the sets back to 1 and say what happened.
 * @param {Event} event
 */
async function onBundleAddedToBasket(event) {
  const { container, message } = /** @type {CustomEvent} */ (event).detail ?? {};
  const widget = container?.isConnected && container.matches(BUNDLE_WIDGET) ? container : document.querySelector(BUNDLE_WIDGET);
  if (!widget) return;
  for (let i = 0; i < 30; i++) {
    const remove = /** @type {HTMLElement | null} */ (widget.querySelector('.bndlr-mnm-selected-products .bndlr-close'));
    if (!remove) break;
    remove.click();
    await new Promise((resolve) => requestAnimationFrame(resolve));
  }
  const input = /** @type {HTMLInputElement | null} */ (widget.querySelector('.bndlr-add-to-cart-quantity-input'));
  if (input && input.value !== '1') {
    input.value = '1';
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }
  setBundleHint(widget, message || 'Added to your basket.', message ? 'warn' : 'done', message ? 12000 : 6000);
}

if (document.querySelector('.cfs-product__app-block')) {
  window.addEventListener('click', guardBundleControl, true);
  window.addEventListener('keydown', guardBundleControl, true);
  document.addEventListener('input', (event) => {
    if (/** @type {Element} */ (event.target)?.closest?.(BUNDLE_WIDGET)) scheduleBundleStock();
  });
  document.addEventListener(CART_STATE_EVENT, scheduleBundleStock);
  document.addEventListener(BUNDLE_ADDED_EVENT, onBundleAddedToBasket);
  const stockWatcher = new MutationObserver((records) => {
    // Bundler re-renders its widget as items are picked; ignore our own notes and messages
    const ours = (/** @type {Node} */ node) => /** @type {Element} */ (node instanceof Element ? node : node.parentElement)?.closest?.('[data-cfs-bundle-stock], [data-cfs-stock-note], [data-cfs-pick-qty]');
    if (records.some((r) => !ours(r.target) && ![...r.addedNodes].every(ours))) scheduleBundleStock();
  });
  for (const block of document.querySelectorAll('.cfs-product__app-block')) stockWatcher.observe(block, { childList: true, subtree: true });
  scheduleBundleStock();
}
